import { ProceedIcon } from '@commenda/icons';
import {
  AddressInput,
  Button,
  Checkbox,
  CountryCode,
  CountryCodeMap,
  Input,
  InputErrorMessage,
  MultiSelect,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SingleCombobox,
  Textarea,
} from '@commenda/kakashi';
import { defaultAddressSchema } from '@commenda/schema/address';
import { IncorporationRequestDataType } from '@commenda/types';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { fetcher } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Address,
  AddressType,
  CorporationType,
  Country,
  Country as CountryList,
  IncorporationRequestStatus,
} from '@prisma/client';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { KeyedMutator } from 'swr';
import {
  CompanyNameInput,
  FormInputWrapper,
  IncorporationFormHeading,
} from '../common';
import { fundingOptions, optionsSSIC, reasonsForOpeningInSG } from './common';
import { CompanyInfoFormFields, companyInfoSchema } from './schema';

enum InvolvedInCryptoOptions {
  Yes = 'yes',
  No = 'no',
}

const MAX_SELECTABLE_COUNTRIES = 3;

const countryOptions = Object.keys(CountryList)
  .filter((key) => key !== CountryList.None && key !== CountryList.Other)
  .map((ctr) => ({
    label: CountryCodeMap[ctr] ?? '',
    value: ctr,
  }))
  .sort((a, b) => a.label.localeCompare(b.label))
  .filter((country) => country.label !== '');

const updateAddresses = (
  formData: CompanyInfoFormFields,
  operatingAddress: Address,
  mailingAddress: Address
) => {
  const addressesToUpsert = [];
  const addressTypesToDelete: AddressType[] = [];

  // Handle operating address - only include if NOT using Commenda's managed address service
  if (!formData.hasOptedForSGRegisteredAddress) {
    addressesToUpsert.push({
      ...(operatingAddress?.id ? { id: operatingAddress.id } : {}),
      type: AddressType.OperatingAddress,
      addressLine1: formData.operatingAddress?.addressLine1,
      addressLine2: formData.operatingAddress?.addressLine2,
      city: formData.operatingAddress?.city,
      country: formData.operatingAddress?.country,
      state: formData.operatingAddress?.state,
      postalCode: formData.operatingAddress?.postalCode,
      ...formData.operatingAddress,
    });
  } else {
    // If user opts for managed service and there was a previous operating address, delete it
    if (operatingAddress?.id) {
      addressTypesToDelete.push(AddressType.OperatingAddress);
    }
  }

  // Handle mailing address changes
  if (formData.isMailingSameAsOperating) {
    // If there was a mailing address before and now we want to remove it
    if (mailingAddress?.id) {
      addressTypesToDelete.push(AddressType.MailingAddress);
    }
  } else {
    // Only update/create mailing address if it's different from operating address
    if (mailingAddress?.id || formData?.mailingAddress?.addressLine1) {
      addressesToUpsert.push({
        ...(mailingAddress?.id ? { id: mailingAddress.id } : {}),
        type: AddressType.MailingAddress,
        addressLine1: formData.mailingAddress?.addressLine1,
        addressLine2: formData.mailingAddress?.addressLine2,
        city: formData.mailingAddress?.city,
        country: formData.mailingAddress?.country,
        state: formData.mailingAddress?.state,
        postalCode: formData.mailingAddress?.postalCode,
      });
    }
  }
  return { addressesToUpsert, addressTypesToDelete };
};

const CompanyInfo = ({
  next,
  data,
  mutate,
  token,
  setNotification,
}: {
  next: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  token: string;
  setNotification: TSetNotification;
}) => {
  const router = useRouter();

  const { id } = router.query;

  const operatingAddress = data?.addresses?.find(
    (address) => address.type === AddressType.OperatingAddress
  )?.value;

  const mailingAddress = data?.addresses?.find(
    (address) => address.type === AddressType.MailingAddress
  )?.value;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    clearErrors,
    watch,
    unregister,
  } = useForm<CompanyInfoFormFields>({
    resolver: zodResolver(companyInfoSchema),
    defaultValues: {
      companyNameOption1: data?.requestData.companyLegalName ?? '',
      companyNameOption2: data?.requestData?.companyLegalName2 ?? '',
      companyNameOption3: data?.requestData?.companyLegalName3 ?? '',
      socialLink: data?.requestData?.socialLink ?? '',
      primarySSIC: data?.requestData.primarySSICCode ?? '',
      secondarySSIC: data?.requestData.secondarySSICCode ?? '',
      businessDescription: data?.requestData.businessDescription ?? '',
      isBusinessInvolvedInCryptoCurrency:
        data?.requestData.isBusinessInvolvedInCryptoCurrency ?? null,
      isBusinessAcceptingCryptocurrencyPayments:
        data?.requestData.isBusinessAcceptingCryptocurrencyPayments ?? false,
      doesBusinessProvideServicesToCryptoCompanies:
        data?.requestData?.doesBusinessProvideServicesToCryptoCompanies ??
        false,
      isBusinessManagingCryptocurrencyCoins:
        data?.requestData?.isBusinessManagingCryptocurrencyCoins ?? false,
      hasOtherReasonForInvolvementInCrypto:
        data?.requestData?.hasOtherReasonForInvolvementInCrypto ?? false,
      reasonForInvolvementInCrypto:
        data?.requestData?.reasonForInvolvementInCrypto ?? '',
      howWillBusinessBeFunded: data?.requestData?.howWillBusinessBeFunded ?? '',
      reasonForIncorporatingInSingapore:
        data?.requestData?.reasonForIncorporatingInSingapore ?? '',
      customReasonForIncorporatingInSingapore:
        data?.requestData?.customReasonForIncorporatingInSingapore ?? '',
      isMailingSameAsOperating:
        data?.requestData?.hasOptedForSGMailingAddress ?? false,
      supportedCountries:
        data?.requestData?.supportedCountries?.split(',') ?? [],
      hasOptedForSGRegisteredAddress:
        data?.requestData?.hasOptedForSGRegisteredAddress ?? false,
      operatingAddress: data?.requestData?.hasOptedForSGRegisteredAddress
        ? null
        : (operatingAddress ?? {
            ...defaultAddressSchema,
            country: Country.SG,
          }),
      mailingAddress:
        mailingAddress ||
        (data?.requestData?.hasOptedForSGMailingAddress === true
          ? null
          : defaultAddressSchema),
    },
  });

  // Unregister operatingAddress if user has opted for managed address service on initial load
  useEffect(() => {
    if (data?.requestData?.hasOptedForSGRegisteredAddress === true) {
      unregister('operatingAddress');
    }
  }, [data?.requestData?.hasOptedForSGRegisteredAddress, unregister]);

  const onSubmit = async (formData: CompanyInfoFormFields) => {
    const { addressesToUpsert, addressTypesToDelete } = updateAddresses(
      formData,
      { ...operatingAddress, country: Country.SG }, // Required
      mailingAddress // Optional (can be removed or added back)
    );

    const payload = {
      requestData: {
        incorporationJurisdiction: 'ADM0_SG',
        corporationType: CorporationType.PteLtd,
        companyLegalName: formData.companyNameOption1,
        companyLegalName2: formData.companyNameOption2,
        companyLegalName3: formData.companyNameOption3,
        socialLink: formData.socialLink,
        primarySSICCode: formData.primarySSIC,
        secondarySSICCode: formData.secondarySSIC,
        businessDescription: formData.businessDescription,
        isBusinessInvolvedInCryptoCurrency:
          formData.isBusinessInvolvedInCryptoCurrency,
        isBusinessAcceptingCryptocurrencyPayments:
          formData.isBusinessAcceptingCryptocurrencyPayments,
        doesBusinessProvideServicesToCryptoCompanies:
          formData.doesBusinessProvideServicesToCryptoCompanies,
        isBusinessManagingCryptocurrencyCoins:
          formData.isBusinessManagingCryptocurrencyCoins,
        hasOtherReasonForInvolvementInCrypto:
          formData.hasOtherReasonForInvolvementInCrypto,
        reasonForInvolvementInCrypto:
          formData.hasOtherReasonForInvolvementInCrypto
            ? formData.reasonForInvolvementInCrypto
            : '',
        howWillBusinessBeFunded: formData.howWillBusinessBeFunded,
        reasonForIncorporatingInSingapore:
          formData.reasonForIncorporatingInSingapore !== 'Other'
            ? formData.reasonForIncorporatingInSingapore
            : 'Other',
        customReasonForIncorporatingInSingapore:
          formData.reasonForIncorporatingInSingapore === 'Other'
            ? formData.customReasonForIncorporatingInSingapore
            : '',
        supportedCountries:
          formData.supportedCountries.length > 0
            ? formData.supportedCountries.join(',')
            : '',
        hasOptedForSGMailingAddress: formData.isMailingSameAsOperating,
        hasOptedForSGRegisteredAddress: formData.hasOptedForSGRegisteredAddress,
      },
      addresses: addressesToUpsert,
      ...(addressTypesToDelete.length > 0 ? { addressTypesToDelete } : []),
      status: IncorporationRequestStatus.InProgress,
    };

    const res = await fetcher(
      `workRequest/${id}`,
      {
        method: 'POST',
        body: { ...payload },
      },
      token
    );

    if (!res.ok) {
      setNotification({
        message:
          'Something went wrong, please try again later or contact Team Commenda if you need help',
        show: true,
        valid: false,
        title: 'Error',
      });
      return;
    }

    await mutate();

    next();
  };

  const willBeInvolvedInCrypto = (isInvolved: boolean) => {
    if (!isInvolved) {
      setValue('isBusinessAcceptingCryptocurrencyPayments', false);
      setValue('doesBusinessProvideServicesToCryptoCompanies', false);
      setValue('isBusinessManagingCryptocurrencyCoins', false);
      setValue('hasOtherReasonForInvolvementInCrypto', false);
      setValue('reasonForInvolvementInCrypto', '');
    }
  };

  return (
    <form
      className="flex flex-col gap-6 max-w-[500px]"
      onSubmit={handleSubmit(onSubmit, (formError: unknown) => {
        console.error(formError);
      })}
    >
      <IncorporationFormHeading text="Company Information" />
      <FormInputWrapper
        id="companyNameOptions"
        text="Legal name of business"
        description="Enter the official, registered name of your business."
        isRequired
      >
        <div>
          <CompanyNameInput
            label="Option 1"
            onValueChange={(val) => {
              setValue('companyNameOption1', val);
              clearErrors('companyNameOption1');
            }}
            error={errors?.companyNameOption1?.message}
            nameValidationOptions={{ country: Country.SG }}
            value={watch('companyNameOption1')}
            placeholder="Entity name 1"
          />
        </div>

        <div>
          <CompanyNameInput
            label="Option 2"
            onValueChange={(val) => {
              setValue('companyNameOption2', val);
              clearErrors('companyNameOption2');
            }}
            error={errors?.companyNameOption2?.message}
            nameValidationOptions={{ country: Country.SG }}
            value={watch('companyNameOption2')}
            placeholder="Entity name 2"
          />
        </div>

        <div>
          <CompanyNameInput
            label="Option 3"
            onValueChange={(val) => {
              setValue('companyNameOption3', val);
              clearErrors('companyNameOption3');
            }}
            error={errors?.companyNameOption3?.message}
            nameValidationOptions={{ country: Country.SG }}
            value={watch('companyNameOption3')}
            placeholder="Entity name 3"
          />
        </div>
      </FormInputWrapper>
      <FormInputWrapper id="socialLink" text="Company website" description="">
        <Input
          type="text"
          {...register('socialLink')}
          placeholder="Company website"
          className="h-11 rounded-md border-border-primary p-2 gap-1.5 bg-bg-primary"
          error={errors?.socialLink?.message}
        />
      </FormInputWrapper>

      <FormInputWrapper
        id="primarySSIC"
        text="What is the primary SSIC business activity?"
        isRequired
      >
        <SingleCombobox
          options={optionsSSIC.map((item) => {
            return {
              label: `${item.ssic_code} - ${item.title}`,
              value: item.ssic_code,
            };
          })}
          value={watch('primarySSIC')}
          setValue={(val) => {
            clearErrors('primarySSIC');
            setValue('primarySSIC', val);
          }}
          placeholder="Select primary SSIC"
          error={errors?.primarySSIC?.message}
          addItemConfig={{
            shouldAllowAdd: false,
            onAddItem: () => {},
            placeholder: '',
          }}
        />
        {errors?.primarySSIC?.message && (
          <InputErrorMessage
            className="-mt-2"
            message={errors?.primarySSIC?.message}
          />
        )}
      </FormInputWrapper>
      <FormInputWrapper
        id="secondarySSIC"
        text="What is the secondary SSIC business activity?"
        isRequired
      >
        <SingleCombobox
          options={optionsSSIC.map((item) => {
            return {
              label: `${item.ssic_code} - ${item.title}`,
              value: item.ssic_code,
            };
          })}
          value={watch('secondarySSIC')}
          setValue={(val) => {
            clearErrors('secondarySSIC');
            setValue('secondarySSIC', val);
          }}
          placeholder="Select secondary SSIC"
          error={errors?.secondarySSIC?.message}
          addItemConfig={{
            shouldAllowAdd: false,
            onAddItem: () => {},
            placeholder: '',
          }}
        />
        {errors?.secondarySSIC?.message && (
          <InputErrorMessage
            className="-mt-2"
            message={errors?.secondarySSIC?.message}
          />
        )}
      </FormInputWrapper>

      <FormInputWrapper
        id="businessDescription"
        text="Detailed description of business activities"
        description="Provide a detailed description of what your business does, including products and services offered."
        isRequired
      >
        <Textarea
          className="py-3 px-[14px] border-border-primary"
          {...register('businessDescription')}
          error={errors?.businessDescription?.message}
          placeholder="Write a description"
        />
      </FormInputWrapper>

      <FormInputWrapper
        id="isBusinessInvolvedInCryptoCurrency"
        text="Will your business be involved in cryptocurrency?"
        isRequired
      >
        <div className="flex flex-col gap-2">
          <RadioGroup
            defaultValue={
              watch('isBusinessInvolvedInCryptoCurrency') === null
                ? undefined
                : watch('isBusinessInvolvedInCryptoCurrency')
                  ? InvolvedInCryptoOptions.Yes
                  : InvolvedInCryptoOptions.No
            }
            className="flex flex-col gap-4"
            onValueChange={(value) => {
              clearErrors('isBusinessInvolvedInCryptoCurrency');
              const isInvolved = value === InvolvedInCryptoOptions.Yes;
              setValue('isBusinessInvolvedInCryptoCurrency', isInvolved);
              willBeInvolvedInCrypto(isInvolved);
            }}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem size="lg" value="yes" id="crypto-yes" />
              <label htmlFor="crypto-yes">Yes</label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem size="lg" value="no" id="crypto-no" />
              <label htmlFor="crypto-no">No</label>
            </div>
          </RadioGroup>
        </div>
        {errors.isBusinessInvolvedInCryptoCurrency && (
          <InputErrorMessage
            message={errors?.isBusinessInvolvedInCryptoCurrency?.message}
          />
        )}
      </FormInputWrapper>

      {watch('isBusinessInvolvedInCryptoCurrency') && (
        <FormInputWrapper
          id="isBusinessAcceptingCryptocurrencyPaymentsWrapper"
          text="How will your business interact with cryptocurrency?"
          description="Please select all that apply"
          isRequired
        >
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isBusinessAcceptingCryptocurrencyPayments"
                size="lg"
                checked={watch('isBusinessAcceptingCryptocurrencyPayments')}
                onCheckedChange={(checked: boolean) => {
                  setValue(
                    'isBusinessAcceptingCryptocurrencyPayments',
                    checked,
                    { shouldValidate: true }
                  );
                  clearErrors('cryptoInteractionOptions');
                }}
              />
              <label
                htmlFor="isBusinessAcceptingCryptocurrencyPayments"
                className="text-sm font-medium"
              >
                Accept cryptocurrency payments
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="doesBusinessProvideServicesToCryptoCompanies"
                size="lg"
                checked={watch('doesBusinessProvideServicesToCryptoCompanies')}
                onCheckedChange={(checked: boolean) => {
                  setValue(
                    'doesBusinessProvideServicesToCryptoCompanies',
                    checked,
                    { shouldValidate: true }
                  );
                  clearErrors('cryptoInteractionOptions');
                }}
              />
              <label
                htmlFor="doesBusinessProvideServicesToCryptoCompanies"
                className="text-sm font-medium"
              >
                Provide services to cryptocurrency companies
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="isBusinessManagingCryptocurrencyCoins"
                size="lg"
                checked={watch('isBusinessManagingCryptocurrencyCoins')}
                onCheckedChange={(checked: boolean) => {
                  setValue('isBusinessManagingCryptocurrencyCoins', checked, {
                    shouldValidate: true,
                  });
                  clearErrors('cryptoInteractionOptions');
                }}
              />
              <label
                htmlFor="isBusinessManagingCryptocurrencyCoins"
                className="text-sm font-medium"
              >
                Operate or manage cryptocurrencies
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="hasOtherReasonForInvolvementInCrypto"
                size="lg"
                checked={watch('hasOtherReasonForInvolvementInCrypto')}
                onCheckedChange={(checked: boolean) => {
                  setValue('hasOtherReasonForInvolvementInCrypto', checked, {
                    shouldValidate: true,
                  });
                  clearErrors('cryptoInteractionOptions');
                }}
              />
              <label
                htmlFor="hasOtherReasonForInvolvementInCrypto"
                className="text-sm font-medium"
              >
                Other
              </label>
            </div>

            {errors.cryptoInteractionOptions && (
              <InputErrorMessage
                message={errors?.cryptoInteractionOptions?.message}
              />
            )}

            {watch('hasOtherReasonForInvolvementInCrypto') && (
              <Input
                type="text"
                {...register('reasonForInvolvementInCrypto')}
                placeholder="Other reason"
                className="h-11 rounded-md border-border-primary p-2 gap-1.5 bg-bg-primary"
                error={errors?.reasonForInvolvementInCrypto?.message}
              />
            )}
          </div>
        </FormInputWrapper>
      )}
      <FormInputWrapper
        id="howWillBusinessBeFunded"
        text="How will your business be funded?"
        isRequired
      >
        <Select
          value={watch('howWillBusinessBeFunded')}
          onValueChange={(val) => {
            clearErrors('howWillBusinessBeFunded');
            setValue('howWillBusinessBeFunded', val);
          }}
        >
          <SelectTrigger error={errors?.howWillBusinessBeFunded?.message}>
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {fundingOptions.map((option) => {
              return (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {errors?.howWillBusinessBeFunded?.message && (
          <InputErrorMessage
            className="-mt-2"
            message={errors?.howWillBusinessBeFunded?.message}
          />
        )}
      </FormInputWrapper>

      <FormInputWrapper
        id="whyOpeningBusinessInSG"
        text="Why are you opening your business in Singapore?"
        isRequired
      >
        <Select
          value={watch('reasonForIncorporatingInSingapore')}
          onValueChange={(val) => {
            clearErrors('reasonForIncorporatingInSingapore');
            setValue('reasonForIncorporatingInSingapore', val);
            if (val !== 'Other')
              setValue('customReasonForIncorporatingInSingapore', '');
          }}
        >
          <SelectTrigger
            error={errors?.reasonForIncorporatingInSingapore?.message}
          >
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {reasonsForOpeningInSG.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!watch('reasonForIncorporatingInSingapore') &&
        errors?.reasonForIncorporatingInSingapore?.message ? (
          <InputErrorMessage
            className="-mt-2"
            message={errors?.reasonForIncorporatingInSingapore?.message}
          />
        ) : null}
        {watch('reasonForIncorporatingInSingapore') === 'Other' ? (
          <Input
            {...register('customReasonForIncorporatingInSingapore')}
            error={errors?.customReasonForIncorporatingInSingapore?.message}
            placeholder="Enter reason for opening business"
          />
        ) : null}
      </FormInputWrapper>

      <FormInputWrapper
        id="threeCountriesBusinessWillOperateIn"
        text="Select up to three countries where you anticipate or already do business."
        isRequired
      >
        <MultiSelect
          id="shareholder-multiselect"
          options={
            countryOptions?.map((c) => {
              return {
                label: c.label,
                value: c.value,
              };
            }) || []
          }
          onValueChange={(val: string[]) => {
            clearErrors('supportedCountries');
            setValue('supportedCountries', val);
          }}
          hideSelectAll
          maxSelectable={MAX_SELECTABLE_COUNTRIES}
          placeholder="Select countries"
          error={errors?.supportedCountries?.message}
          addItemConfig={{
            shouldAllowAdd: false,
            onAddItem: () => {},
          }}
          defaultValue={watch('supportedCountries') ?? []}
        />
        {errors?.supportedCountries?.message && (
          <InputErrorMessage
            className="-mt-2"
            message={errors?.supportedCountries?.message}
          />
        )}
      </FormInputWrapper>

      <FormInputWrapper
        id="hasOptedForSGRegisteredAddress"
        text="Are you using Commenda's registered address service?"
        description="If you're not sure, you can talk to your Commenda account manager. If yes, we will use Commenda's Singapore address as your registered office address."
      >
        <RadioGroup
          value={
            watch('hasOptedForSGRegisteredAddress') === true ? 'yes' : 'no'
          }
          className="flex flex-col gap-4"
          onValueChange={(val: 'no' | 'yes') => {
            if (val === 'no') {
              setValue('hasOptedForSGRegisteredAddress', false);
              setValue('operatingAddress', {
                ...defaultAddressSchema,
                country: Country.SG,
              });
            } else {
              setValue('hasOptedForSGRegisteredAddress', true);
              unregister('operatingAddress');
              clearErrors('operatingAddress');
            }
          }}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem size="lg" value="yes" id="registered-address-yes" />
            <label htmlFor="registered-address-yes">Yes</label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem size="lg" value="no" id="registered-address-no" />
            <label htmlFor="registered-address-no">No</label>
          </div>
        </RadioGroup>
      </FormInputWrapper>

      {!watch('hasOptedForSGRegisteredAddress') ? (
        <FormInputWrapper
          id="operatingAddress"
          text="Registered office address"
          description="This must be in Singapore."
          isRequired
        >
          <AddressInput
            id="operatingAddress"
            address={watch('operatingAddress') as Address}
            disabled={{ country: true }}
            defaultCountry={Country.SG}
            onChange={(ad: Address) => {
              const update = {
                ...ad,
                addressLine2: ad.addressLine2 ?? null,
              };
              setValue('operatingAddress', update);
            }}
            error={{
              country:
                !watch('operatingAddress.country') &&
                errors?.operatingAddress?.country?.message
                  ? errors?.operatingAddress?.country?.message
                  : null,
              addressLine1:
                !watch('operatingAddress.addressLine1') &&
                errors?.operatingAddress?.addressLine1?.message
                  ? errors?.operatingAddress?.addressLine1?.message
                  : null,
              city:
                !watch('operatingAddress.city') &&
                errors?.operatingAddress?.city?.message
                  ? errors?.operatingAddress?.city?.message
                  : null,
              postalCode:
                !watch('operatingAddress.postalCode') &&
                errors?.operatingAddress?.postalCode?.message
                  ? errors?.operatingAddress?.postalCode?.message
                  : null,
              state:
                !watch('operatingAddress.state') &&
                errors?.operatingAddress?.state?.message
                  ? errors?.operatingAddress?.state?.message
                  : undefined,
            }}
          />
        </FormInputWrapper>
      ) : null}

      <FormInputWrapper
        id="isMailingSameAsOperating"
        text="Business address"
        description="This should be the address where you conduct your primary business activities. This cannot be Commenda’s registered address."
      >
        <div className="flex items-center space-x-2">
          <Checkbox
            id="isMailingSameAsOperating"
            size="lg"
            checked={watch('isMailingSameAsOperating')}
            onCheckedChange={() => {
              setValue(
                'isMailingSameAsOperating',
                !watch('isMailingSameAsOperating')
              );

              if (watch('isMailingSameAsOperating')) {
                clearErrors('mailingAddress');
                setValue('mailingAddress', null);
              } else {
                setValue('mailingAddress', defaultAddressSchema);
              }
            }}
          />
          <label
            htmlFor="isMailingSameAsOperating"
            className="text-sm font-medium"
          >
            Business address is same as registered office address
          </label>
        </div>
      </FormInputWrapper>

      {!watch('isMailingSameAsOperating') ? (
        <AddressInput
          id="mailingAddress"
          address={watch('mailingAddress') as Address}
          onChange={(ad: Address) => {
            const update = {
              ...ad,
              country: ad.country as CountryCode,
              addressLine2: ad.addressLine2 ?? null,
            };
            setValue('mailingAddress', update);
          }}
          error={{
            country:
              errors?.mailingAddress?.country?.message &&
              !watch('mailingAddress.country')
                ? errors?.mailingAddress?.country?.message
                : undefined,
            addressLine1:
              !watch('mailingAddress.addressLine1') &&
              errors?.mailingAddress?.addressLine1?.message
                ? errors?.mailingAddress?.addressLine1?.message
                : null,
            city:
              !watch('mailingAddress.city') &&
              errors?.mailingAddress?.city?.message
                ? errors?.mailingAddress?.city?.message
                : null,
            postalCode:
              !watch('mailingAddress.postalCode') &&
              errors?.mailingAddress?.postalCode?.message
                ? errors?.mailingAddress?.postalCode?.message
                : null,
            state:
              !watch('mailingAddress.state') &&
              errors?.mailingAddress?.state?.message
                ? errors?.mailingAddress?.state?.message
                : undefined,
          }}
        />
      ) : null}

      <Button type="submit" disabled={isSubmitting} className="w-fit">
        <span>Continue</span>
        <ProceedIcon className="ml-2 h-4 w-4" />
      </Button>
    </form>
  );
};

export default CompanyInfo;
